/**
 * Φίλτρα του πλέγματος (§13.11).
 *
 * ΓΙΑΤΙ το «απόκρυψη ειδών προσφοράς» είναι ΕΝΕΡΓΟ εξ ορισμού: κάθε είδος που
 * μπαίνει σε προσφορά 1+1 υπάρχει ΔΕΥΤΕΡΗ φορά σε κρυφή κατηγορία προσφοράς, με
 * ίδιο όνομα και ίδια τιμή (§13.1). Χωρίς το φίλτρο, το πλέγμα δείχνει διπλές
 * γραμμές «Freddo espresso» που ο χειριστής δεν αναγνωρίζει — και η πρώτη του
 * σκέψη είναι να «διορθώσει» τη μία. Οι κρυφές γραμμές παραμένουν προσιτές με ένα
 * κλικ, και τα δίδυμα φαίνονται ούτως ή άλλως ως σήμα «×2» στο κύριο κελί.
 */

import React from 'react';
import { RefreshCw, Search } from 'lucide-react';

export interface GridFilters {
  category: string;
  q: string;
  only_unavailable: boolean;
  price_differs: boolean;
  missing_in: boolean;
  has_edits: boolean;
  structure_differs: boolean;
  twins_only: boolean;
  hide_offer_items: boolean;
}

export const DEFAULT_FILTERS: GridFilters = {
  category: '',
  q: '',
  only_unavailable: false,
  price_differs: false,
  missing_in: false,
  has_edits: false,
  structure_differs: false,
  twins_only: false,
  hide_offer_items: true,
};

const TOGGLES: Array<{ key: keyof GridFilters; label: string; title: string }> = [
  { key: 'only_unavailable', label: 'Μόνο μη διαθέσιμα', title: 'Είδη που είναι κλειστά τουλάχιστον σε ένα κατάστημα' },
  { key: 'price_differs', label: 'Όπου διαφέρει η τιμή', title: 'Η ίδια γραμμή έχει πάνω από μία τιμή στην αλυσίδα' },
  { key: 'missing_in', label: 'Λείπει από ≥1', title: 'Υπάρχει σε κάποια καταστήματα και λείπει από άλλα' },
  { key: 'has_edits', label: 'Με εκκρεμείς αλλαγές', title: 'Γραμμές με πρόχειρη αλλαγή ή σύγκρουση' },
  { key: 'structure_differs', label: '≠ δομή', title: 'Κάποιο κατάστημα έχει διαφορετικά tiers/επιλογές από την επικρατούσα δομή' },
  { key: 'twins_only', label: 'Μόνο δίδυμα', title: 'Είδη που υπάρχουν δεύτερη φορά σε κατηγορία προσφοράς' },
];

interface Props {
  filters: GridFilters;
  categories: string[];
  loading: boolean;
  total: number;
  shown: number;
  onChange: (next: GridFilters) => void;
  onReload: () => void;
}

const CatalogFilters: React.FC<Props> = ({ filters, categories, loading, total, shown, onChange, onReload }) => {
  const set = <K extends keyof GridFilters>(key: K, value: GridFilters[K]) => {
    onChange({ ...filters, [key]: value });
  };

  return (
    <div className="flex flex-wrap items-end gap-3 bg-surface-1/40 border border-line rounded-xl p-3">
      <div className="min-w-[180px]">
        <label className="block text-xs text-t3 mb-1">Κατηγορία</label>
        <select
          value={filters.category}
          onChange={e => set('category', e.target.value)}
          className="w-full bg-surface-0 border border-line-strong rounded-lg px-3 py-2 text-sm text-t1"
        >
          <option value="">Όλες</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="flex-1 min-w-[200px]">
        <label className="block text-xs text-t3 mb-1">Αναζήτηση είδους</label>
        <div className="relative">
          <Search className="w-4 h-4 text-t4 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={filters.q}
            onChange={e => set('q', e.target.value)}
            placeholder="π.χ. κουλούρι"
            className="w-full bg-surface-0 border border-line-strong rounded-lg pl-9 pr-3 py-2 text-sm text-t1"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {TOGGLES.map(t => (
          <label key={String(t.key)} className="flex items-center gap-2 text-sm text-t2" title={t.title}>
            <input
              type="checkbox"
              checked={!!filters[t.key]}
              onChange={e => set(t.key, e.target.checked as any)}
              className="w-4 h-4 rounded"
            />
            {t.label}
          </label>
        ))}
        <label
          className="flex items-center gap-2 text-sm text-t2"
          title="Οι κρυφές κατηγορίες προσφοράς έχουν ΔΕΥΤΕΡΟ αντίγραφο του ίδιου είδους με ίδια τιμή. Κρύβονται εξ ορισμού ώστε το πλέγμα να δείχνει το εμπορικό μενού."
        >
          <input
            type="checkbox"
            checked={filters.hide_offer_items}
            onChange={e => set('hide_offer_items', e.target.checked)}
            className="w-4 h-4 rounded"
          />
          Απόκρυψη ειδών προσφοράς
        </label>
      </div>

      <button
        type="button"
        onClick={onReload}
        className="px-3 py-2 text-sm bg-surface-2 hover:bg-surface-3 text-t1 rounded-lg flex items-center gap-2 h-[38px]"
      >
        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Ανανέωση πλέγματος
      </button>

      <span className="text-xs text-t3 h-[38px] flex items-center">
        {shown} από {total} γραμμές
      </span>
    </div>
  );
};

export default CatalogFilters;
